import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { TabContainer, Tab } from '@ui5/webcomponents-react';
import { useAuth } from '../../store/auth';
import { PERMISSIONS } from '@b1links/core';
import { MenuAdmin } from './MenuAdmin';
import { MenuDesigner } from './MenuDesigner';
import { RolesAdmin } from './RolesAdmin';
import { UsersAdmin } from './UsersAdmin';
import { ScreensAdmin } from './ScreensAdmin';
import { SystemAdmin } from './SystemAdmin';
import { KpisAdmin } from './KpisAdmin';
import { CompaniesAdmin } from './CompaniesAdmin';
import { PacksAdmin } from './PacksAdmin';
import { ScreenAccessAdmin } from './ScreenAccessAdmin';

const TABS = [
  { key: 'screens', text: 'Screens', icon: 'screen-split-two' },
  { key: 'menu', text: 'Menu & Access', icon: 'menu' },
  { key: 'layout', text: 'Menu Layout', icon: 'chart-tree-map' },
  { key: 'packs', text: 'Packs', icon: 'add-product' },
  { key: 'roles', text: 'Roles', icon: 'role' },
  { key: 'users', text: 'App Users', icon: 'employee' },
  { key: 'access', text: 'Screen Access', icon: 'locked' },
  { key: 'kpis', text: 'Dashboard KPIs', icon: 'bar-chart' },
  { key: 'companies', text: 'Companies', icon: 'building', super: true },
  { key: 'system', text: 'System', icon: 'synchronize' },
] as const;

/** Administration workspace (admin permission required). Tabs: menu structure, roles, users, companies. */
export function AdminPage() {
  const { tab = 'screens' } = useParams();
  const nav = useNavigate();
  const can = useAuth(s => s.can);
  const principal = useAuth(s => s.principal);
  const isSuper = !!(principal && (principal.superUser || principal.mode === 'sap' || principal.permissions.includes('*')));
  if (!can(PERMISSIONS.ADMIN_USERS)) return <Navigate to="/" replace />;
  const tabs = TABS.filter(t => !('super' in t && t.super) || isSuper);
  // Guard against deep-linking a superuser-only tab without the right role.
  if (tab === 'companies' && !isSuper) return <Navigate to="/admin" replace />;

  return (
    <div>
      <TabContainer collapsed onTabSelect={e => nav(`/admin/${(e.detail.tab as HTMLElement).dataset.key}`)}>
        {tabs.map(t => <Tab key={t.key} text={t.text} icon={t.icon} data-key={t.key} selected={tab === t.key} />)}
      </TabContainer>
      <div style={{ padding: '0.5rem 0.25rem' }}>
        {tab === 'screens' && <ScreensAdmin />}
        {tab === 'menu' && <MenuAdmin />}
        {tab === 'layout' && <MenuDesigner />}
        {tab === 'packs' && <PacksAdmin />}
        {tab === 'roles' && <RolesAdmin />}
        {tab === 'users' && <UsersAdmin />}
        {tab === 'access' && <ScreenAccessAdmin />}
        {tab === 'kpis' && <KpisAdmin />}
        {tab === 'companies' && <CompaniesAdmin />}
        {tab === 'system' && <SystemAdmin />}
      </div>
    </div>
  );
}
